import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormArray, FormGroup, UntypedFormBuilder, Validators} from '@angular/forms';

import {ActivatedRoute, Router} from '@angular/router';
import {WingameService} from 'src/app/shared/services/wingame.api';
import {DialogService} from 'src/app/shared/services/dialog.service';
import {DialogConfirmComponent} from 'src/app/shared/ui/components/dialogs/dialog-confirm/dialog-confirm.component';
import { HttpParams } from '@angular/common/http';
import {TargetAudienceService} from 'src/app/shared/target-audience/target-audience.service';
import {ProductService} from 'src/app/shared/services/product.service';
import {NotificationsService} from 'src/app/admin/notifications/services/notifications.service';
import {NewProductService} from 'src/app/admin/shop/products/new-product/new-product.service';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {LayoutAlertService} from 'src/app/admin/layout-alert.service';

@Component({
    selector: 'app-notifications-shared',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    standalone: false
})
export class NotificationsSharedComponent implements OnInit, OnDestroy {
  dataLoaded = false;
  form: FormGroup;
  wingameUuid: string;
  hiddenWingame: boolean;

  messageTypes = [
    {value: 1, text: 'pre-defined'},
    {value: 2, text: 'custom'},
  ];
  predefMessagesLoaded = false;
  predefMessages;
  expirationInfo = [
    {text: '', language: 'Dutch'},
    {text: '', language: 'English'},
  ];
  currentTabLang: string = 'Dutch';
  selectedTargetAudience = {included: {}, excluded: {}};
  predefinedAudience = {included: {}, excluded: {}};
  targetAudience;
  targetAudienceId;

  soundOptions = [
    {value: 0, text: 'Default value'},
    {value: 1, text: 'No sound'},
  ];

  soundOptionsNames = ['Default value', 'No sound'];
  dataSource;
  eventId;

  deeplinks = {
    wingame: 'beappic://v2/win/', // requires game uuid
    event: 'beappic://event/', // requires event id
    app2xpr: 'app2xpr://event/', // requires event id
  };
  statusAllowPublish = false;
  showTA = false;
  statusId;
  initialSoundOption = 1;
  blockSendButton = false;
  requestId;
  selectedPredefinedMessage;

  private destroy$ = new Subject<void>();

  @Input() id;
  @Input() createMode;
  @Input() updateMode = false;
  @Input() verbFilters;

  @Output() formValidity = new EventEmitter();
  @Output() emitWingameStatus = new EventEmitter();
  @Output() enablePublishBtn = new EventEmitter();

  constructor(
    private fb: UntypedFormBuilder,
    private notificationsService: NotificationsService,
    private route: ActivatedRoute,
    private router: Router,
    private wingameService: WingameService,
    private dialogService: DialogService,
    private layoutAlertService: LayoutAlertService,
    private targetAudienceService: TargetAudienceService,
    private productService: ProductService,
    private newProductService: NewProductService,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['id']) {
        this.statusId = params['id'];
      }
    });
    if (this.id) {
      this.start();
    }

    this.requestId = this.route.snapshot.queryParamMap.get('request');
    if (this.requestId) {
      this.notificationsService.getRequestData(this.requestId).subscribe(data => this.prefillRequestData(data));
    }
  }

  start() {
    this.form = this.createForm();
    this.getEventId();

    this.setValidators();
  }

  prefillRequestData(data) {
    this.createMode = true;

    if (data.notification_input.notifications.en.verbId) {
      this.form.get('message_type').setValue('pre-defined');
      this.form.get('pre_defined_message').patchValue(data.notification_input.notifications.en.verbId);
      this.selectedPredefinedMessage = data.notification_input.notifications.en.verbId;
    } else {
      this.form.get('message_type').setValue('custom');
      const abbrvToFull = {
        en: 'English',
        nl: 'Dutch',
      };
      const expirationInfoArray = this.form.get('message') as FormArray;
      expirationInfoArray.clear();
      for (let lang of ['en', 'nl']) {
        const expirationGroup = this.fb.group({
          text: [data.notification_input.notifications[lang].customText],
          display: [true],
          language: [abbrvToFull[lang]],
        });
        expirationInfoArray.push(expirationGroup);
      }
    }
    let deadline = new Date(data.deadline);
    deadline.setHours(17);
    deadline.setMinutes(0);
    this.form.get('scheduled_at').setValue(deadline);
    this.form.get('if_scheduled').setValue(true);

    this.setDeeplink();
  }

  setDeeplink() {
    if (this.verbFilters === 'win_games') {
      const event_id = this.eventId;
      let deeplink;
      if (this.hiddenWingame || !event_id) {
        deeplink = `${this.deeplinks.wingame}${this.wingameUuid}`;
      } else {
        deeplink = `${this.deeplinks.event}${event_id}`;
      }
      this.form.patchValue({deeplink});
    } else if (this.verbFilters === 'products') {
      this.form.patchValue({
        deeplink: `${this.deeplinks.event}${this.eventId}`,
      });
    }
  }

  getNotificationDeliveryStatuses() {
    let actorObjk;
    let params;
    if (this.verbFilters === 'win_games') {
      actorObjk = {object_id: this.id, type: 'wingame'};
      const actorString = JSON.stringify(actorObjk);
      params = new HttpParams().set('actor', actorString).set('page', '1');
    } else if (this.verbFilters === 'products') {
      actorObjk = {object_id: this.eventId, type: 'event'};
      const actorString = JSON.stringify(actorObjk);
      params = new HttpParams().set('actor', actorString).set('page', '1').set('category', 'products');
    }

    if (actorObjk) {
      this.notificationsService.deliveryStatuses(params).subscribe((data: any) => {
        this.dataSource = data.results;

        if (this.updateMode) {
          const notificationToEdit = this.dataSource.find(item => item.id === +this.statusId);
          const messByLang = notificationToEdit.messages_by_lang;
          const messages = [];

          messages.push({
            text: messByLang.en ? messByLang.en : '',
            display: !!messByLang.en,
            language: 'English',
          });

          messages.push({
            text: messByLang.nl ? messByLang.nl : '',
            display: !!messByLang.nl,
            language: 'Dutch',
          });

          this.form.get('message').setValue(messages);
          this.form.get('message_type').setValue('custom');
          this.form.get('sound').setValue(notificationToEdit.sound_name === 'default' ? 0 : 1);
          this.initialSoundOption = notificationToEdit.sound_name === 'default' ? 0 : 1;
          if (notificationToEdit.status === 'scheduled') {
            this.form.get('if_scheduled').setValue(true);
            this.form.get('scheduled_at').setValue(this.convertDateFormat(notificationToEdit.scheduled_at));
          }
          this.notificationsService.targetAudiences(notificationToEdit.target_audience).subscribe((data: any) => {
            this.form
              .get('target_audience')
              .setValue(this.targetAudienceService.stringifiedAudience(data.audience_conf));
            this.selectedTargetAudience = data.audience_conf;
          });
          this.form.get('display_on_event').setValue(notificationToEdit.display_on_event);
          this.form.get('existing_target_audience_id').setValue(notificationToEdit.target_audience);
          this.form.get('pre_defined_message').setValue(notificationToEdit.verb.id);
          //  this.updateAllFieldsValueAndValidity();

          this.dataLoaded = true;
        } else {
          this.dataLoaded = true;
        }
      });
    }
  }

  convertDateFormat(originalDate: string): string {
    const date = new Date(originalDate);
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    const hours = ('0' + date.getHours()).slice(-2);
    const minutes = ('0' + date.getMinutes()).slice(-2);

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  updateAllFieldsValueAndValidity() {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control.updateValueAndValidity();
    });
  }

  getEventId() {
    if (this.verbFilters === 'win_games') {
      this.wingameService.getDetails(this.id).subscribe(data => {
        const event = data.event;
        this.emitWingameStatus.emit(data.status);
        if (data.status !== '3') {
          this.statusAllowPublish = true;
          this.enablePublishBtn.emit(false);
        } else {
          this.checkForPublishBtn(this.id);
        }
        if (event) {
          this.form.get('event').setValue(event?.id_event);
          this.eventId = event.id_event;
          this.setPredefinedTargetAudience(data);
          this.setDeeplink();
        }
        this.getNotificationDeliveryStatuses();
        this.wingameUuid = data.uuid;
        this.hiddenWingame = data.hidden_wingame;
        if (!this.updateMode) {
          this.dataLoaded = true;
        }
        window.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      });
    } else if (this.verbFilters === 'products') {
      this.productService.getProductsById(this.id).subscribe((data: any) => {
        this.eventId = data.relation.id;
        this.form.get('event').setValue(this.eventId);
        this.setPredefinedTargetAudience(data);
        this.setDeeplink();
        this.getNotificationDeliveryStatuses();
        if (data.is_published !== null) {
          this.newProductService.setIsPublished(data.is_published);
        }
        if (data.wingame_product !== null) {
          this.newProductService.setIsWingameProd(data.wingame_product);
        }
      });
    }
  }

  checkForPublishBtn(gameId) {
    this.wingameService.getWinGamePrizeSettings(gameId).subscribe(
      data => {
        if (data.assigned.length > 0) {
          this.enablePublishBtn.emit(true);
        } else {
          this.enablePublishBtn.emit(false);
        }
      },
      error => {
        console.error('Error during getting Wingame prizes data.', error);
      },
    );
  }

  setPredefinedTargetAudience(data) {
    let targetAudience;
    if (this.verbFilters === 'win_games') {
      targetAudience = this.targetAudienceService.targetAudiencePredefinedEventObj(data);
    } else if (this.verbFilters === 'products') {
      targetAudience = this.targetAudienceService.targetAudiencePredefinedEventObjFromProduct(data);
    }
    this.selectedTargetAudience = targetAudience;
    const targetAudienceData = this.targetAudienceService.stringifiedAudience(targetAudience);
    this.form.get('target_audience').setValue(targetAudienceData);
  }

  getPredefinedMessages() {
    this.notificationsService.getNotificationsVerbs({verb_filters: this.verbFilters}).subscribe((data: any) => {
      this.predefMessages = data.results;
    });
  }

  createForm(createMode?: boolean) {
    const currentDate = new Date();
    const formGroup = this.fb.group({
      actor: {type: 'wingame', id: this.id},
      message_type: ['', Validators.required],
      pre_defined_message: [null],
      event: [null],
      deeplink: ['', Validators.required],
      sound: [1, Validators.required],
      scheduled_at: [currentDate.toISOString().slice(0, 16)],
      if_scheduled: [false],
      message: this.fb.array([]),
      target_audience: [
        createMode ? this.targetAudienceService.stringifiedAudience(this.selectedTargetAudience) : '',
        Validators.required,
      ],
      existing_target_audience_id: [null],
      display_on_event: [null],
      verb_group: 3,
    });

    if (this.eventId) {
      formGroup.get('event').setValue(this.eventId);
    }

    formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.formValidity.emit(formGroup.valid);
    });

    const expirationInfoArray = formGroup.get('message') as FormArray;

    this.expirationInfo.forEach((info, index) => {
      const lang = info.language.toLowerCase();
      const expirationGroup = this.fb.group({
        text: [info.text],
        display: [false],
        language: [info.language],
      });
      expirationInfoArray.push(expirationGroup);
    });

    expirationInfoArray.controls.forEach((control: FormGroup) => {
      control
        .get('display')
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe(value => {
          const firstAvailableLang = this.findAvailableLang();
          if (firstAvailableLang) {
            this.currentTabLang = firstAvailableLang.value.language;
          }
        });
    });

    formGroup
      .get('message_type')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (data === 'pre-defined' && !this.predefMessagesLoaded) {
          this.predefMessagesLoaded = true;
          this.getPredefinedMessages();
        }
        const preDefinedMessage = formGroup.get('pre_defined_message');
        preDefinedMessage.clearValidators();
        if (data === 'pre-defined') {
          preDefinedMessage.setValidators(Validators.required);
        }
        preDefinedMessage.updateValueAndValidity();
      });

    formGroup
      .get('if_scheduled')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (!data) {
          formGroup.get('scheduled_at').setValue(currentDate.toISOString().slice(0, 16));
        }
      });

    formGroup.get('message_type').valueChanges.subscribe(data => {
      if (data === 'pre-defined' && !this.predefMessagesLoaded) {
        this.predefMessagesLoaded = true;
        this.getPredefinedMessages();
      }
      const preDefinedMessage = formGroup.get('pre_defined_message');
      preDefinedMessage.clearValidators();
      if (data === 'pre-defined') {
        preDefinedMessage.setValidators(Validators.required);
      }
      preDefinedMessage.updateValueAndValidity();
    });

    formGroup.get('if_scheduled').valueChanges.subscribe(data => {
      if (!data) {
        formGroup.get('scheduled_at').setValue(currentDate.toISOString().slice(0, 16));
        formGroup.get('scheduled_at').clearValidators();
        formGroup.get('scheduled_at').updateValueAndValidity();
      } else {
        formGroup.get('scheduled_at').setValidators(Validators.required);
        formGroup.get('scheduled_at').updateValueAndValidity();
      }
    });

    return formGroup;
  }

  setValidators() {
    const ifScheduled = this.form.get('if_scheduled');
    const scheduledAt = this.form.get('scheduled_at');
    ifScheduled.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      if (value) {
        scheduledAt.setValidators(Validators.required);
        scheduledAt.updateValueAndValidity();
      } else {
        scheduledAt.clearValidators();
        scheduledAt.updateValueAndValidity();
      }
    });
  }

  onTextSelected(selectedText: string) {
    this.form.get('pre_defined_message').setValue(selectedText);
  }

  findAvailableLang(): FormGroup | null {
    const firstFormGroupWithDisplayTrue = this.expirationInfoFA.controls.find((control: FormGroup) => {
      return control.get('display').value === true;
    });
    return firstFormGroupWithDisplayTrue ? (firstFormGroupWithDisplayTrue as FormGroup) : null;
  }

  anyDisplayTrue(): boolean {
    const expirationInfoArray = this.form.get('message') as FormArray;
    for (let i = 0; i < expirationInfoArray.length; i++) {
      const expirationGroup = expirationInfoArray.at(i) as FormGroup;
      if (expirationGroup.get('display').value) {
        return true;
      }
    }
    return false;
  }

  showTab(tabName: string) {
    this.currentTabLang = tabName;
  }

  onAudienceSaved(audience, forAlreadyAddedNotif?: boolean) {
    if (forAlreadyAddedNotif) {
      this.notificationsService.updateAudience(this.targetAudienceId, audience).subscribe(
        data => {
          this.layoutAlertService.success("Notification's Target Audience saved.", 2);
        },
        error => {
          this.layoutAlertService.error(
            'Error during saving Target Audience. If this situation persists, contact your administrator.',
          );
          console.error('Error while saving Target Audience:', error);
        },
      );
    } else {
      if (Object.keys(audience.included).length > 0 || Object.keys(audience.excluded).length > 0) {
        this.targetAudience = audience;
        this.form.get('target_audience').setValue(audience);
      } else {
        this.targetAudience = null;
      }
    }
  }

  prepareRequest(request) {
    const req = {...request};

    if (this.verbFilters === 'products') {
      ((req.verb_label = 3), delete req.actor);
    }

    if (req.message_type === 'pre-defined') {
      req.message = {};
      req.message_type = 'pre_defined';
    } else if (req.message_type === 'custom') {
      const mess = [...req.message];
      let messOb = {nl: '', en: ''};
      mess.forEach(item => {
        if (item.language === 'Dutch' && item.display) {
          messOb.nl = item.text;
        } else if (item.language === 'English' && item.display) {
          messOb.en = item.text;
        }
      });
      req.message = messOb;
      delete req.pre_defined_message;
    }

    if (!req.if_scheduled) {
      req.scheduled_at = null;
    }

    if (!this.eventId) {
      delete req.event;
    }

    req['push_request_id'] = this.requestId;

    delete req.existing_target_audience_id;
    delete req.display_on_event;
    delete req.if_scheduled;
    return req;
  }

  publish() {
    this.wingameService.publishWingame(this.id).subscribe(
      (data: any) => {
        const messageData = {
          title: 'Publish Wingame',
          descriptionHtml: 'Wingame published.',
          confirmBtn: 'OK',
        };

        this.callDialog(messageData);
      },
      error => {
        const messageData = {
          title: 'Publish Error',
          descriptionHtml: 'Error while publishing Wingame.',
          confirmBtn: 'OK',
        };
        this.callDialog(messageData);
        console.error('Error while saving wingame:', error);
      },
    );
  }

  next() {
    this.blockSendButton = true;
    const req = this.prepareRequest(this.form.value);
    if (this.id) {
      this.wingameService.saveNotificationsSettings(this.id, req).subscribe(
        (data: any) => {
          const messageData = {
            title: 'Saving Wingame',
            descriptionHtml: 'Wingame notification saved.',
            confirmBtn: 'OK',
          };
          this.callDialog(messageData);

          this.clearForm(this.createMode);
          this.blockSendButton = false;
          this.router.navigate([], {
            queryParams: {
              request: null,
            },
            queryParamsHandling: 'merge',
          });
          if (this.updateMode) {
            this.router.navigate(['admin', 'notifications', 'delivery-statuses']);
          }
        },
        error => {
          let err_message = error;
          if ('non_field_errors' in error?.error) {
            err_message = error.error.non_field_errors[0];
            this.layoutAlertService.error(`Error during saving Wingame notifications. \n Error: ${err_message}`);
          } else {
            this.layoutAlertService.error(
              `Error during saving Wingame notifications. If this situation persists, contact your administrator. \n Error: ${err_message}`,
            );
          }
          this.blockSendButton = false;

          console.error('Error while saving wingame:', error);
        },
      );
    }
  }

  update() {
    if (!this.form.get('if_scheduled').value) {
      this.next();
    } else {
      const targetAudience = this.form.get('target_audience').value
        ? this.targetAudienceService.stringifiedAudience(this.form.get('target_audience').value)
        : {
            excluded: {},
            included: {},
          };
      const request = {
        delivery_status_id: this.statusId,
        message: this.form.get('message').value,
        message_type: this.form.get('message_type').value,
        pre_defined_message: this.form.get('pre_defined_message').value,
        verb_group: this.form.get('verb_group').value,
        sound: this.form.get('sound').value,
        existing_target_audience_id: this.form.get('existing_target_audience_id').value,
        target_audience: targetAudience,
        scheduled_at: this.form.get('if_scheduled').value ? this.form.get('scheduled_at').value : null,
        display_on_event: this.form.get('display_on_event').value,
        actor: this.form.get('actor').value,
        deeplink: this.form.get('deeplink').value,
      };
      if (request.actor.id) {
        request.actor.uuid = Number(request.actor.id);
        request.actor.id = Number(request.actor.id);
      }
      if (request.message_type === 'pre-defined') {
        request.message = {};
        request.message_type = 'pre_defined';
      } else if (request.message_type === 'custom') {
        const mess = [...request.message];
        let messOb = {nl: '', en: ''};
        mess.forEach(item => {
          if (item.language === 'Dutch' && item.display) {
            messOb.nl = item.text;
          } else if (item.language === 'English' && item.display) {
            messOb.en = item.text;
          }
        });
        request.message = messOb;
      }

      this.updateNotification(request);
    }
  }

  updateNotification(request) {
    this.notificationsService.updateNotifications(request).subscribe(
      response => {
        return this.notificationsService.getNextPage(response);
      },
      error => {
        this.layoutAlertService.error(
          `Error during updating Wingame notification. If this situation persists, contact your administrator. \n Error: ${error}`,
        );
        console.error('Error while during wingame notification:', error);
      },
    );
  }

  callDialog = data => {
    const dialogRef = this.dialogService.openDialog(DialogConfirmComponent, {
      width: '670px',
      data: data,
      panelClass: ['cdk-mobile-full-screen', 'cdk-mobile-full-screen-vertical-center'],
    });

    setTimeout(() => {
      dialogRef.close();
    }, 3000);
  };

  showTargetAudience(id, updateMode?: boolean) {
    this.targetAudienceId = id;
    this.notificationsService.targetAudiences(id).subscribe(
      (data: any) => {
        if (updateMode) {
          this.selectedTargetAudience = data.audience_conf;
        } else {
          this.predefinedAudience = data.audience_conf;
        }
      },
      error => {
        this.layoutAlertService.error(
          'Error during getting Wingame target audience. If this situation persists, contact your administrator.',
        );
        console.error('Error while getting Wingame target audience:', error);
      },
    );
  }

  cancelNotification(notifId) {
    this.notificationsService.cancelNotification(notifId).subscribe(
      data => {
        this.layoutAlertService.success('Notification canceled.', 2);
        this.getNotificationDeliveryStatuses();
      },
      error => {
        this.layoutAlertService.error(
          'Error during canceling notification. If this situation persists, contact your administrator.',
        );
        console.error('Error while canceling notification:', error);
      },
    );
  }

  soundSelected(event) {
    const item = this.soundOptions.find(option => option.text === event);
    this.form.get('sound').setValue(item.value);
  }

  goToCreateMode() {
    this.createMode = true;
    this.updateMode = false;
    this.clearForm(true);
    this.removeIdQueryParam();
  }

  removeIdQueryParam() {
    const queryParams = {...this.route.snapshot.queryParams};
    delete queryParams.id;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
    });
  }

  clearForm(createMode?: boolean) {
    if (!createMode) {
      this.selectedTargetAudience = {included: {}, excluded: {}};
    } else {
      this.getEventId();
    }

    this.form.reset();
    this.form = this.createForm(createMode);
    this.setDeeplink();
  }

  goToStatusesMode() {
    this.createMode = false;
    this.updateMode = false;
    this.getNotificationDeliveryStatuses();
    this.removeIdQueryParam();
  }

  checkForm() {
    console.log('selectedTargetAudience', this.selectedTargetAudience);
    console.log('form', this.form);
    console.log('form message', JSON.stringify(this.form.get('message').value));
  }

  get expirationInfoFA() {
    return this.form.get('message') as FormArray;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
